import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { useParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';

import {
  EmptyFeed,
  FeedListItem,
  FeedListInfinitySpinner,
  Button,
  LoaderDashboard,
} from 'components';

import { useFeatureFlagState } from 'context';
import { toTitleCase } from 'helpers/string';
import { GroupPinnedFeedQuery } from 'services/apollo/queries';
import { handleFetchMoreError } from 'services/apollo';
import { useQueryLifecycle } from 'hooks/useQueryLifecycle';
import { trackingGroupPinnedFeed } from 'services';

import styles from './GroupActivityFeed.module.scss';

interface GroupPinnedFeedProps {
  /** @string Group name used for error handling and message */
  groupName: string;
  /** @boolean If the owned feed is visible or not */
  visible?: boolean;
  /** @function Callback function to toggle the pinned feed visibility */
  toggleVisibility?: () => void;
}

export const GroupPinnedFeed: React.FC<GroupPinnedFeedProps> = ({
  visible,
  groupName,
  toggleVisibility,
}) => {
  const { groupId } = useParams();
  const { groupsFeatureFlag } = useFeatureFlagState();
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const { error, data, fetchMore, networkStatus } = useQuery(GroupPinnedFeedQuery, {
    variables: { groupId, includeGroups: groupsFeatureFlag, first: 10, cursor: '' },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const { isCached: showingCachedData } = useQueryLifecycle(networkStatus);

  /**
   * Analytics tracking
   */
  useEffect(() => {
    if (!showingCachedData) {
      const totalNodes = data?.groupById?.activityFeed?.pageInfo?.total;
      const memberCount = data?.groupById?.members?.length;
      const groupName = data?.groupById?.name;
      const groupType = data?.groupById?.groupType;
      trackingGroupPinnedFeed(totalNodes, memberCount, groupName, groupType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showingCachedData]);

  if (data === undefined && visible)
    return (
      <div className={styles.tetrisMain}>
        <div className={styles.headingWrapper}>
          <h3 className={styles.heading}>Pinned</h3>
        </div>
        <LoaderDashboard layout="HeadlessFeed" />
      </div>
    );

  /**
   * Loads a new page of pinned documents
   */
  const loadMoreDocuments = async () => {
    setIsLoadingMore(true);
    const { endCursor } = data.groupById.pinnedNodes.pageInfo;

    try {
      await fetchMore({
        variables: { cursor: endCursor },
        updateQuery: (prevResult: any, { fetchMoreResult }) => {
          fetchMoreResult.profileByIdentifier.ownsNodes.nodes = [
            ...prevResult.groupById.pinnedNodes.nodes,
            ...fetchMoreResult.groupById.pinnedNodes.nodes,
          ];

          setIsLoadingMore(false);
          return fetchMoreResult;
        },
      });
    } catch (e) {
      handleFetchMoreError(e);
    }
  };

  return (
    <AnimatePresence initial={false}>
      {visible && (
        <motion.div
          className={styles.feedWrapper}
          exit={{ opacity: 0, height: 0 }}
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
        >
          <div className={styles.headingWrapper}>
            <h3 className={styles.heading}>Pinned</h3>
            <Button
              type="Utility"
              onClick={() => {
                toggleVisibility && toggleVisibility();
              }}
            >
              Hide
            </Button>
          </div>

          {error ? (
            <EmptyFeed
              message={
                'There was a problem loading your pinned documents in ' + toTitleCase(groupName)
              }
            />
          ) : !data.groupById.pinnedNodes.nodes.length ? (
            <EmptyFeed
              message={'You have no pinned documents in ' + toTitleCase(data.groupById.name)}
            />
          ) : (
            <>
              {data.groupById.pinnedNodes.nodes.map((node: any) => (
                <FeedListItem
                  node={node}
                  nodeId={node.id}
                  nodeType={node.__typename}
                  key={node.id}
                  name={node.title}
                  baseUrl={node.baseUrl}
                  url={node.url}
                  pins={node.pins}
                  isPinnable={true}
                  isIntegrationConnected={node.isIntegrationConnected}
                  integrationType={node.integrationType}
                  owner={node.owners[0]}
                  collaborators={node.accessorInfo}
                  modified={node.modified}
                  created={node.created}
                  nodeIcon={node.category}
                  isShareable={node.sharingEnabled}
                  shareTargets={node.shareTargets}
                />
              ))}
            </>
          )}

          {!isLoadingMore ? (
            data?.groupById?.pinnedNodes?.pageInfo?.hasNextPage && (
              <Button
                className={styles.feedButton}
                type="Utility"
                onClick={() => {
                  if (!isLoadingMore) {
                    loadMoreDocuments();
                  }
                }}
              >
                Load More
              </Button>
            )
          ) : (
            <FeedListInfinitySpinner isLoadingMore={isLoadingMore} />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
